import type { EventsClient } from '../events-client.js';
import type { MenuController } from '../menus/menu-controller.js';
import Logger from './logger.js';

const MODULE = 'UserMessaging';

interface MessageEntry {
    text: string;
    remainingTime: number;
}

/**
 * Centralized user-facing messaging router.
 * Manages up to 3 recent messages with individual timeout tracking.
 * Messages are joined by newlines when displayed.
 */
export class UserMessaging {
    private static _instance: UserMessaging;

    private eventsClient: EventsClient | null = null;
    private menuController: MenuController | null = null;
    private pendingMessages: string[] = [];
    private messageQueue: MessageEntry[] = [];
    private lastStateMessage: string | null = null;
    private updateInterval: number | null = null;
    private hudSuppressedLast = false;
    private lastHudDisplayText = '';

    static get instance(): UserMessaging {
        if (!UserMessaging._instance) {
            UserMessaging._instance = new UserMessaging();
        }
        return UserMessaging._instance;
    }

    /**
     * Register EventsClient singleton for XR notifications.
     */
    registerEventsClient(client: EventsClient): void {
        this.eventsClient = client;
        this.flushPending();
        this.startUpdateLoop();
    }

    /**
     * Register MenuController singleton for non-XR footer messages.
     */
    registerMenuController(menu: MenuController): void {
        this.menuController = menu;
        this.flushPending();
        this.startUpdateLoop();
    }

    /**
     * Notify the user via the correct UI channel.
     * Message is added to queue and displayed for the specified duration.
     * @param message - Text to display
     * @param duration - Display duration in seconds (default: 3.0)
     */
    notify(message: string, duration: number = 3.0): void {
        if (!message) return;

        const now = new Date();

        // Define options for formatting the time only (no date)
        const options: Intl.DateTimeFormatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true // Use 12-hour clock with AM/PM
        };

        // Create a DateTimeFormat instance for a specific locale (e.g., 'en-US')
        const formatter = new Intl.DateTimeFormat('en-US', options);

        // Format the time
        const humanReadableTime = formatter.format(now);

        this.addMessage("[" + humanReadableTime + "] " + message, duration);
    }

    /**
     * Notify only when the message differs from last state message.
     * @param message - Text to display
     * @param duration - Display duration in seconds (default: 3.0)
     */
    notifyState(message: string, duration: number = 3.0): void {
        if (this.lastStateMessage === message) return;
        this.lastStateMessage = message;
        this.notify(message, duration);
    }

    /**
     * Add a message to the queue and update display.
     * Keeps up to 3 most recent messages.
     */
    private addMessage(message: string, duration: number): void {
        this.messageQueue.push({ text: message, remainingTime: duration });
        
        // Keep only the 3 most recent messages
        if (this.messageQueue.length > 3) {
            this.messageQueue.shift();
        }

        this.updateDisplay();
    }

    /**
     * Update message timers and remove expired messages.
     */
    private tick(deltaTime: number): void {
        // Update remaining time for all messages
        for (const entry of this.messageQueue) {
            entry.remainingTime -= deltaTime;
        }

        // Remove expired messages
        this.messageQueue = this.messageQueue.filter(entry => entry.remainingTime > 0);

        // Update display with remaining messages
        this.updateDisplay();
    }

    /**
     * Clear all messages from the queue and update display.
     */
    clearMessages(): void {
        this.messageQueue = [];
        this.updateDisplay();
    }

    /**
     * Build the display string from all active messages joined by newlines.
     */
    private buildDisplayString(): string {
        return this.messageQueue.map(entry => entry.text).join('\n');
    }

    /**
     * Update both UI channels with the current message display.
     */
    private updateDisplay(): void {
        const displayText = this.buildDisplayString();
        const suppressHudInVr = this.menuController?.isVrMenuActive?.() ?? false;

        if (this.eventsClient?.notify) {
            if (suppressHudInVr) {
                // Ensure HUD text is cleared immediately when menu-based display takes over.
                if (!this.hudSuppressedLast && this.lastHudDisplayText.length > 0) {
                    this.eventsClient.notify('');
                }
                this.lastHudDisplayText = '';
            } else if (displayText !== this.lastHudDisplayText || this.hudSuppressedLast) {
                this.eventsClient.notify(displayText);
                this.lastHudDisplayText = displayText;
            }
        }

        this.hudSuppressedLast = suppressHudInVr;

        if (this.menuController?.displayFooterMessage) {
            this.menuController.displayFooterMessage(displayText);
        }
    }

    /**
     * Start the update loop that manages message timeouts.
     * Runs only once across the singleton's lifetime.
     */
    private startUpdateLoop(): void {
        if (this.updateInterval !== null) {
            return; // Already running
        }

        // Update every 50ms (20 times per second) for smooth timeout handling
        this.updateInterval = window.setInterval(() => {
            this.tick(0.05);
        }, 50) as any;
    }

    private flushPending(): void {
        if (!this.pendingMessages.length) return;
        const queued = [...this.pendingMessages];
        this.pendingMessages = [];
        for (const message of queued) {
            this.addMessage(message, 3.0);
        }
    }
}
